import { clamp } from 'lodash'

export type coord = [x: number, y: number]
export enum Direction {
  'top',
  'right',
  'bottom',
  'left',
}

/**
 * Given a coordinate and a `targetRect`, return a new coordinate projected into the `targetRect`. Optionally takes a
 * `scrollingRect` which should be used if your `targetRect` lies within a container that can scroll.
 * @param coord a coord in the form `[x,y]`. You most likely want to pass `[MouseEvent.pageX, MouseEvent.pageY]`
 * @param targetRect coordinates should be clamped so that they lie within this `DOMRect`
 * @param scrollingRect the `DOMRect` of the scrolling container in which `targetRect` lies. Defaults to `document.body`
 * if not provided.
 * @returns a coord in the form `[x,y]`, clamped to lie within `targetRect`
 * @see {@link https://codesandbox.io/s/clampcoordtorect-y2pi9w| CodeSandbox example}
 */
export const clampCoordToRect = (
  [x, y]: coord,
  targetRect: Pick<DOMRect, 'top' | 'left' | 'width' | 'height'>,
  scrollingRect: Pick<DOMRect, 'top' | 'left'> = document.body.getBoundingClientRect(),
): coord => {
  const yRectTop = targetRect.top - scrollingRect.top
  const xRectLeft = targetRect.left - scrollingRect.left

  const clampRect = {
    top: yRectTop,
    bottom: yRectTop + targetRect.height - 1,
    left: xRectLeft,
    right: xRectLeft + targetRect.width - 1,
  }

  return [clamp(x, clampRect.left, clampRect.right), clamp(y, clampRect.top, clampRect.bottom)]
}

/**
 * Given a `coord` and a `DOMRect`, return the closest side of the `DOMRect` to the `coord`.
 * @param coord a coordinate in the form `[x,y]`
 * @param rect a DOMRect-like object, with `top`, `left`, `width`, and `height` properties
 * @returns an enum representing the closest direction
 */
export const getClosestSide = (
  [x, y]: coord,
  { top, right, bottom, left }: Pick<DOMRect, 'top' | 'right' | 'bottom' | 'left'>,
) => {
  const topDist = Math.abs(y - top)
  const rightDist = Math.abs(x - right)
  const bottomDist = Math.abs(y - bottom)
  const leftDist = Math.abs(x - left)

  if (topDist <= Math.min(bottomDist, leftDist, rightDist)) return Direction.top
  if (rightDist <= Math.min(topDist, bottomDist, leftDist)) return Direction.right
  if (bottomDist <= Math.min(topDist, leftDist, rightDist)) return Direction.bottom
  if (leftDist <= Math.min(topDist, bottomDist, rightDist)) return Direction.left
  return Direction.top // should never happen, it's here to satisfy typescript
}

/**
 * Given a pair of coordinates, return the distance between them.
 * @param A The first coordinate
 * @param B The second coordinate
 * @returns The (always positive) distance between the two coordinates
 */
export const getDistance = ([x1, y1]: coord, [x2, y2]: coord) => Math.sqrt((x2 - x1) ** 2 + (y2 - y1) ** 2)
